import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import DatePicker from 'react-datepicker';
import CampoEditableNumero from './CampoEditableNumero.js';
import LargoCampos from '../../largoCampos/LargoCampos.js';
import SelectMes from '../../selectores/SelectMes.js';
import * as Iconos from '../../UI/Iconos.js';
import * as UI from '../../constantes/UI.js';
import * as FuncionesFecha from '../../funciones/FuncionesFecha.js';
import styles from './CampoEditableFechaCampos.module.css';

/**
 * Componente input personalizado con máscara dd/MM/yyyy para usar con DatePicker
 */
const InputConMascara = React.forwardRef((props, ref) => {
  const inputRef = useRef(ref);
  const prevValueRef = useRef('');
  const { onTabPress, value, ...restProps } = props;

  // Sincronizar input cuando cambia value del exterior
  useEffect(() => {
    if (inputRef.current && value !== undefined && value !== null) {
      // Solo actualizar si el valor externo cambió significativamente
      const valorActual = inputRef.current.value;
      const soloDigitosActual = valorActual.replace(/\D/g, '');
      const soloDigitosNuevo = (value || '').replace(/\D/g, '');

      // Si los dígitos son diferentes, actualizar el input
      if (soloDigitosActual !== soloDigitosNuevo) {
        inputRef.current.value = value || '';
        prevValueRef.current = value || '';
      }
    }
  }, [value]);

  const aplicarFormato = (soloDigitos) => {
    let valorFormateado = soloDigitos;
    if (soloDigitos.length >= 2) {
      valorFormateado = soloDigitos.slice(0, 2) + '/' + soloDigitos.slice(2);
    }
    if (soloDigitos.length >= 4) {
      valorFormateado =
        soloDigitos.slice(0, 2) + '/' + soloDigitos.slice(2, 4) + '/' + soloDigitos.slice(4);
    }
    return valorFormateado;
  };

  const handleChange = (e) => {
    let valor = e.target.value;
    const prevValue = prevValueRef.current;
    let nuevaPosicionCursor = null;

    // Extraer solo dígitos
    const soloDigitos = valor.replace(/\D/g, '');
    const prevDigitos = (prevValue || '').replace(/\D/g, '');

    // Limita a 8 dígitos máximo
    if (soloDigitos.length > 8) {
      valor = aplicarFormato(soloDigitos.slice(0, 8));
      e.target.value = valor;
      prevValueRef.current = valor;
      restProps.onChange?.(e);
      return;
    }

    // Si hay espacios (dígitos faltantes), mantener estructura y posicionar cursor
    if (valor.includes(' ')) {
      // Si aumentó el número de dígitos, usuario escribió un dígito nuevo
      if (soloDigitos.length > prevDigitos.length) {
        // El navegador ya insertó el dígito, NO reformatear
        // Solo posicionar cursor en el próximo espacio (donde continuará escribiendo)
        const proximoEspacio = valor.indexOf(' ');
        nuevaPosicionCursor = proximoEspacio >= 0 ? proximoEspacio : valor.length;
      } else if (soloDigitos.length === prevDigitos.length) {
        // No cambió el número de dígitos, validar que solo haya caracteres permitidos
        if (!/^[\d/ ]*$/.test(valor)) {
          e.target.value = prevValue;
          return;
        }
      }
    } else {
      // Sin espacios, aplicar formato normal (máscara completa)
      valor = aplicarFormato(soloDigitos);
    }

    // Actualizar el valor del input
    e.target.value = valor;

    // Posicionar el cursor si es necesario
    if (nuevaPosicionCursor !== null) {
      e.target.setSelectionRange(nuevaPosicionCursor, nuevaPosicionCursor);
    }

    // Actualizar ref de valor anterior
    prevValueRef.current = valor;

    // Disparar onChange
    restProps.onChange?.(e);
  };

  const handleKeyDown = (e) => {
    // Si presiona Tab, siempre cierra el calendario y pasa al siguiente campo
    if (e.key === 'Tab') {
      // Llama al callback para cerrar el DatePicker
      onTabPress?.(e);
      // Permite que Tab funcione normalmente
      return;
    }

    // Si presiona backspace
    if (e.key === 'Backspace') {
      e.preventDefault();

      const input = e.target;
      const cursorPos = input.selectionStart;
      const valor = input.value;

      // Encontrar el dígito anterior al cursor
      let posABorrar = -1;
      for (let i = cursorPos - 1; i >= 0; i--) {
        if (/\d/.test(valor[i])) {
          posABorrar = i;
          break;
        }
      }

      if (posABorrar >= 0) {
        // Extraer solo los dígitos
        const soloDigitos = valor.replace(/\D/g, '');

        // Mapear posición en vista a índice en array de dígitos
        let indiceDigito = 0;
        for (let i = 0; i < posABorrar; i++) {
          if (/\d/.test(valor[i])) {
            indiceDigito++;
          }
        }

        // Eliminar ese dígito del array
        const nuevosSoloDigitos =
          soloDigitos.slice(0, indiceDigito) + soloDigitos.slice(indiceDigito + 1);

        // Reformatear
        let nuevoValor = aplicarFormato(nuevosSoloDigitos);

        // Trim espacios finales
        nuevoValor = nuevoValor.trimEnd();

        input.value = nuevoValor;
        prevValueRef.current = nuevoValor;

        // Posicionar el cursor correctamente:
        // Si hay espacio en el lugar donde se borró, cursor justo antes del espacio
        // Si no hay espacio (fue el último), cursor al final
        let nuevaPosicion = posABorrar;
        if (posABorrar < nuevoValor.length && nuevoValor[posABorrar] === ' ') {
          nuevaPosicion = posABorrar;
        } else {
          // El cursor va al final o antes del siguiente separador
          nuevaPosicion = Math.min(posABorrar, nuevoValor.length);
        }

        input.setSelectionRange(nuevaPosicion, nuevaPosicion);

        // Crear un evento sintético que se parezca a uno real de change
        const syntheticEvent = {
          target: input,
          currentTarget: input,
          type: 'change',
        };

        // Disparar onChange con el evento sintético
        restProps.onChange?.(syntheticEvent);
      }
    }
  };

  return (
    <input
      {...restProps}
      ref={inputRef}
      type="text"
      placeholder="dd/MM/yyyy"
      maxLength="10"
      autoComplete="off"
      defaultValue={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
});

InputConMascara.displayName = 'InputConMascara';

const Accion = {
  ANIO: 'anio',
  MES: 'mes',
  DIA: 'dia',
  FECHA_TEXTO: 'textoFecha',
  FECHA_VALIDA: 'fechaValida',
  PARCIAL: 'parcial',
  ACTUALIZAR: 'actualizar',
};

const FechaBase = (anioActual, mesActual, diaActual) => {
  const mesReal = mesActual ? mesActual - 1 : null;
  return {
    anio: anioActual,
    mes: mesReal,
    dia: diaActual,
    fechaTexto: FuncionesFecha.CamposToFechaCorta(anioActual, mesReal, diaActual),
    parcial: anioActual ? !diaActual || !mesActual : false,
  };
};

const ReducerFecha = (state, action) => {
  switch (action.type) {
    case Accion.ANIO: {
      return {
        ...state,
        fechaTexto: FuncionesFecha.CamposToFechaCorta(action.value, state.mes, state.dia),
        anio: action.value,
      };
    }
    case Accion.MES: {
      return {
        ...state,
        fechaTexto: FuncionesFecha.CamposToFechaCorta(state.anio, action.value, state.dia),
        mes: action.value,
      };
    }
    case Accion.DIA: {
      return {
        ...state,
        fechaTexto: FuncionesFecha.CamposToFechaCorta(state.anio, state.mes, action.value),
        dia: action.value,
      };
    }
    case Accion.FECHA_VALIDA: {
      const campos = FuncionesFecha.FechaCortaToCampos(action.value);
      return {
        ...state,
        fechaTexto: action.value,
        anio: campos[0],
        mes: campos[1],
        dia: campos[2],
      };
    }
    case Accion.PARCIAL: {
      let fecha = '';
      fecha =
        action.value === true
          ? FuncionesFecha.CamposToFechaCorta(state.anio, state.mes, null)
          : FuncionesFecha.CamposToFechaCorta(state.anio, state.mes, state.dia ? state.dia : 1);
      return {
        ...state,
        fechaTexto: fecha,
        parcial: action.value,
      };
    }
    case Accion.ACTUALIZAR: {
      return action.value;
    }
    default: {
      return FechaBase(null, null, null);
    }
  }
};

const CampoEditableFechaCampos = (props) => {
  const nombre = props.nombre;
  const anio = props.anio;
  const mes = props.mes ? props.mes + 1 : null;
  const dia = props.dia;
  const editar = props.editar;
  const actualizar = props.actualizar;
  const anioFijo = props.anioFijo; // Año fijo (no editable)
  const altBotonParcial = props.altBotonParcial || 'Fecha parcial';
  const tabStopParcial = props.tabStopParcial !== false;
  const [fecha, setFecha] = useReducer(ReducerFecha, FechaBase(anio, mes, dia));
  const contenedorRef = useRef(null);
  const focoPendienteRef = useRef(false);
  const datePickerRef = useRef(null);
  const { esMobile } = UI.useUI();
  const mostrarControlParcial = props.mostrarControlParcial !== false || esMobile;
  const usarBotonParcial = props.usarBotonParcial === true || esMobile;

  const handleTabPress = () => {
    // Cierra el DatePicker cuando se presiona Tab
    if (datePickerRef.current) {
      datePickerRef.current.setOpen?.(false);
    }
  };

  const actualizarReducer = useCallback(() => {
    setFecha({ type: Accion.ACTUALIZAR, value: FechaBase(anio, mes, dia) });
  }, [anio, mes, dia]);

  useEffect(() => {
    actualizarReducer();
  }, [actualizar, actualizarReducer]);

  const onChangeFechaHandler = (fecha) => {
    if (fecha) {
      // Si anioFijo está definido, validar que la fecha sea de ese año
      if (anioFijo && fecha.getFullYear() !== anioFijo) {
        alert(`La fecha debe ser del año ${anioFijo}`);
        return;
      }
      setFecha({ type: Accion.FECHA_VALIDA, value: FuncionesFecha.FechaCorta(fecha) });
      props.onChange(nombre, FuncionesFecha.DateToArray(fecha));
    }
  };

  const onChangeParcialHandler = () => {
    focoPendienteRef.current = true;
    setFecha({ type: Accion.PARCIAL, value: !fecha.parcial });
    if (fecha.anio) {
      props.onChange(nombre, [fecha.anio, fecha.mes, null]);
    } else {
      props.onChange(nombre, [null, null, null]);
    }
  };

  useEffect(() => {
    if (!focoPendienteRef.current || !editar || !contenedorRef.current) {
      return;
    }

    const selector = fecha.parcial ? 'select' : 'input';
    const primerCampoVisible = contenedorRef.current.querySelector(selector);
    if (primerCampoVisible && typeof primerCampoVisible.focus === 'function') {
      primerCampoVisible.focus();
    }

    focoPendienteRef.current = false;
  }, [fecha.parcial, editar]);

  const onChangeAnio = (nombreCampo, anioElegido) => {
    // Si anioFijo está definido, usar ese año en lugar del elegido
    const anioFinal = anioFijo || anioElegido;
    setFecha({ type: Accion.ANIO, value: anioFinal });
    if (anioFinal) {
      props.onChange(nombre, [anioFinal, fecha.mes, null]);
    } else {
      props.onChange(nombre, [null, null, null]);
    }
  };

  const onMesElegido = (mesElegido) => {
    const valor = mesElegido ? Number(mesElegido) : null;
    setFecha({ type: Accion.MES, value: valor });
    if (fecha.anio) {
      props.onChange(nombre, [fecha.anio, valor, null]);
    } else {
      props.onChange(nombre, [null, null, null]);
    }
  };

  let camposFecha = '';
  if (editar) {
    if (fecha.parcial) {
      camposFecha = (
        <React.Fragment>
          <SelectMes
            className={`${styles.campoMes}`}
            mesActual={fecha.mes}
            onMesElegido={onMesElegido}
          />
          <CampoEditableNumero
            estilo={`${styles.campoAnio}`}
            nombre={nombre}
            valor={anioFijo || fecha.anio}
            largo={LargoCampos.ANIO}
            editar={!anioFijo}
            onChange={onChangeAnio}
          />
        </React.Fragment>
      );
    } else {
      camposFecha = (
        <DatePicker
          ref={datePickerRef}
          customInput={
            <InputConMascara className={styles.campoFecha} onTabPress={handleTabPress} />
          }
          key={nombre}
          selected={FuncionesFecha.FechaCortaToDate(fecha.fechaTexto)}
          dateFormat="dd/MM/yyyy"
          utcOffset={0}
          onChange={onChangeFechaHandler}
          shouldCloseOnSelect={true}
        />
        /*        <input
          className={`${styles.campoFecha}`}
          key={nombre}
          value={fecha.fechaTexto}
          onChange={onChangeFechaHandler}
        />*/
      );
    }
  } else {
    camposFecha = fecha.fechaTexto;
  }

  return (
    <div className={`${styles.filaDatos}`} ref={contenedorRef}>
      {camposFecha}
      {editar && mostrarControlParcial && (
        <React.Fragment>
          {usarBotonParcial ? (
            <button
              type="button"
              className={`${styles.botonParcial} ${fecha.parcial ? styles.botonParcialPresionado : ''}`}
              onClick={onChangeParcialHandler}
              tabIndex={tabStopParcial ? 0 : -1}
              title="Fecha parcial"
              aria-pressed={fecha.parcial}
            >
              <img
                src={Iconos.fechaParcial}
                alt={altBotonParcial}
                className={styles.iconoParcial}
              />
            </button>
          ) : (
            <React.Fragment>
              <input type="checkbox" checked={fecha.parcial} onChange={onChangeParcialHandler} />
              <label className={`${styles.textoParcial}`}>Parcial</label>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default CampoEditableFechaCampos;
